// ContactUs.tsx
import React, { useEffect, useState } from "react";
import { CrossIcon, EmailCon, StartIocn, TextBlockICon, UseriCon } from "../../constants/svgPath";
import imagePath from "../../constants/imagePath";
import PhoneInput from "react-phone-input-2";
import { contactUs } from "../../redux/actions/settings";
import { useSelector } from "react-redux";
import { countryToDial } from "../../constants/constant";
import Turnstile from "../Turnstile/Turnstile";

interface ContactUsProps {
  isOpen: boolean;
  onClose: () => void;
}
type FieldName = "name" | "email" | "phone" | "message" | "budget" | "countryCode";
type FormValues = Record<FieldName, string>;
type FormErrors = Record<FieldName, string>;
type TouchedState = Record<FieldName, boolean>;

const initialValues: FormValues = { name: "", email: "", phone: "", message: "", budget: '$5000 - $10000', countryCode: '' };
const initialErrors: FormErrors = { name: "", email: "", phone: "", message: "", budget: '', countryCode: '' };
const initialTouched: TouchedState = { name: false, email: false, phone: false, message: false, budget: false, countryCode: false };

const ContactUs: React.FC<ContactUsProps> = ({ isOpen, onClose }) => {
  const {countryData} = useSelector((state:any)=>state.settings)

  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>(initialErrors);
  const [touched, setTouched] = useState<TouchedState>(initialTouched);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileError, setTurnstileError] = useState("");
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [defaultCountryCode,setDefaultCountryCode]=useState(String(countryData?.country_code).toLocaleLowerCase())

  const handleChange = (field: FieldName, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: FieldName) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = { ...initialErrors };
    let valid = true;
    const { name, email, phone, message, budget } = values;

    if (!name.trim()) {
      newErrors.name = "Name is required.";
      valid = false;
    } else if (name.length > 15) {
      newErrors.name = "Name must be 15 characters or less.";
      valid = false;
    }

    if (!email.trim()) {
      newErrors.email = "Email is required.";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Enter a valid email address.";
      valid = false;
    }

    const digits = phone.replace(/\D/g, "");
    if (!digits) {
      newErrors.phone = "Phone number is required.";
      valid = false;
    } else if (digits.length < 10) {
      newErrors.phone = "Enter a valid phone number.";
      valid = false;
    }

    if (!message.trim()) {
      newErrors.message = "Message is required.";
      valid = false;
    } else if (message.length > 250) {
      newErrors.message = "Message cannot exceed 250 characters.";
      valid = false;
    }

    if (!budget.trim()) {
      newErrors.message = "Budget is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };



  // IMPORTANT: Close must call parent onClose so parent can hide modal (isOpen -> false)
  const handleClose = (e?: React.MouseEvent) => {
    if (e && e.preventDefault) e.preventDefault();

    // clear validation/flags first
    setErrors(initialErrors);
    setTouched(initialTouched);
    setSubmitted(false);
    setTurnstileToken("");
    setTurnstileError("");

    // optionally clear values on close:
    // setValues(initialValues);

    // inform parent to close modal
    onClose();
  };

  const handleSubmit = (event?: React.FormEvent) => {
    if (event) event.preventDefault();

    setSubmitted(true);
    setTouched({ name: true, email: true, phone: true, message: true, budget: true, countryCode: true });

    if (!validate()) return;

    if (!turnstileToken) {
      setTurnstileError("Please complete the security check before submitting.");
      return;
    }

    {
      const { name, email, phone, message, countryCode,budget } = values;
      setLoading(true)
      
      const apiData = {
        name: name,
        email: email,
        phoneNumber: phone.substring(countryCode.length),
        countryCode: `+${countryCode}`,
        description: message,
        deviceInfo: navigator.userAgent,
        budget: budget
      }

      contactUs(apiData, { "Content-Type": "application/json", "X-Turnstile-Token": turnstileToken }).then((res:any) => {
        alert('Thank you!! One of Our Experts Will Get in Touch With You Shortly')
      }).catch((error) => {
       alert(error?.message)
      }).finally(() => {
        setLoading(false)
        // reset local form state (optional)
        setValues(initialValues);
        setErrors(initialErrors);
        setTouched(initialTouched);
        setSubmitted(false);
        setTurnstileToken("");
        setTurnstileReset((current) => current + 1);

        // close modal via parent
        onClose();
      })
      // 👇 send data to API or backend here
    };
  }

  
  useEffect(()=>{
    setDefaultCountryCode(String(countryData?.country_code).toLocaleLowerCase())
    handleChange('countryCode',countryToDial[countryData?.country_code as keyof typeof countryToDial] ?? "91"); 
  },[countryData])


  


  const showError = (field: FieldName): string | null => {
    return (touched[field] || submitted) && errors[field] ? errors[field] : null;
  };

  if (!isOpen) return null;

  return (
    <div className={`pop_overlay ${isOpen ? "open" : ""}`}>
      <div className="pop_main">
        <section className="pop_sec">
          {/* Close button — make sure type="button" */}
          <button type="button" className="cross_icon" onClick={handleClose}>
            <CrossIcon />
          </button>

          <div className="pop_l">
            <div className="next_heading">
              <h3>
                <StartIocn />
                Let’s Work Together!
              </h3>
              <h2>Bring Your Vision to Life with Industry Leaders.</h2>
              <p>
                Got a project in mind? Share your ideas with us—our expert IT team is ready to
                bring them to life.
              </p>
            </div>
            <figure className="img_pop">
              <img src={imagePath.PopupImg} alt="" />
            </figure>
          </div>

          <div className="pop_r">
            <div className="commn_frm_innr">
              {/* Name Input */}
              <div className="inp_grp">
                <span className="inp_icon">
                  <UseriCon />
                </span>
                <input
                  type="text"
                  placeholder="Your Name"
                  className="inp_frm"
                  value={values.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  onBlur={() => handleBlur("name")}
                  maxLength={15}
                />
                {showError("name") && <p className="txt_vaild">{showError("name")}</p>}
              </div>

              {/* Email Input */}
              <div className="inp_grp">
                <span className="inp_icon">
                  <EmailCon />
                </span>
                <input
                  type="email"
                  placeholder="Email Address"
                  className="inp_frm"
                  value={values.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  onBlur={() => handleBlur("email")}
                />
                {showError("email") && <p className="txt_vaild">{showError("email")}</p>}
              </div>

              {/* Phone Input */}
              <div className="inp_grp">
                <PhoneInput
                  country={defaultCountryCode.toLocaleLowerCase()}
                  value={values.phone}
                  onChange={(value, country:any) => { 
                    handleChange('countryCode',country?.dialCode); 
                    handleChange('phone',value);  
                }}
                  onBlur={() => handleBlur("phone")}
                  dropdownDirection="top"
                />
                {showError("phone") && <p className="txt_vaild">{showError("phone")}</p>}
              </div>

              {/* Message Input */}
              <div className="text_area">
                <span className="inp_icon">
                  <TextBlockICon />
                </span>
                <textarea
                  className="inp_frm"
                  placeholder="Write Message Here..."
                  rows={3}
                  cols={40}
                  value={values.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  onBlur={() => handleBlur("message")}
                  maxLength={250}
                />
                {showError("message") && <p className="txt_vaild">{showError("message")}</p>}
              </div>

              <div className="select_value">
                <h3>Please Select Your Budget</h3>
                <div className="list_value">
                  <button type="button" className={values.budget == '$5000 - $10000' ? "active" : ''} onClick={() => handleChange("budget", '$5000 - $10000')}>
                    $5000 - $10000
                  </button>
                  <button type="button" className={values.budget == '$10000 - $25000' ? "active" : ''} onClick={() => handleChange("budget", '$10000 - $25000')}>$10000 - $25000</button>
                  <button type="button" className={values.budget == '$25000 - $50000' ? "active" : ''} onClick={() => handleChange("budget", '$25000 - $50000')}>$25000 - $50000</button>
                  <button type="button" className={values.budget == '$50000+' ? "active" : ''} onClick={() => handleChange("budget", '$50000+')}>$50000+</button>
                </div>
              </div>

              <Turnstile
                className="turnstile-widget--modal"
                onVerify={(token) => {
                  setTurnstileToken(token);
                  setTurnstileError("");
                }}
                onError={() => setTurnstileError("Security verification is unavailable. Please refresh and try again.")}
                resetSignal={turnstileReset}
              />
              {turnstileError && <p className="turnstile-error">{turnstileError}</p>}

              {/* Submit Button */}
              <div className="inp_grp inp_grp_btn">
                <button disabled={loading || !turnstileToken} type="button" className={`btn_common btn_primry ${loading&& 'btn_min_200width'}`} onClick={handleSubmit}>
                {loading ? ( <span className="spinner"></span> ) :' Submit Now' }
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactUs;
